import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Collapse,
  Chip,
  Divider,
  useTheme,
  Grid2 as Grid,
  Tooltip
} from '@mui/material';
import Button from 'components/Common/Button';
import { useNavigate } from 'react-router-dom';
import { MdBusiness, MdExpandLess, MdExpandMore, MdOutlineEdit } from 'react-icons/md';
import { FaRegCopy } from 'react-icons/fa';
import { useDeleteBusinessPlan, useGetBusinessPackages } from '@/apis/agency/businessPackages/hook';
import { RiHardDrive3Line, RiInboxArchiveLine } from 'react-icons/ri';
import { FiClock, FiLock } from 'react-icons/fi';
import PackageLoader from './PackageLoader';
import { formatDollars } from '@/utils/helperFunctions';
import { GoGlobe } from 'react-icons/go';
import { LuSettings } from 'react-icons/lu';
import ChatLogsIcon from '@/assets/icons/ChatLogsIcon';
import PackageLocationIcon from '@/assets/icons/PackageLocationIcon';
import AddonsConcurrencyIcon from '@/assets/icons/AddonsConcurrencyIcon';

const Packages = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { data: packagesData, isLoading } = useGetBusinessPackages();
  const { mutateAsync: deleteBusinessPlan } = useDeleteBusinessPlan();
  const [openPackage, setOpenPackage] = useState<string | null>(null);

  const handleToggle = (id: string) => {
    setOpenPackage((prev) => (prev === id ? null : id));
  };

  const cardStyle = {
    width: '100%',
    bgcolor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    fontWeight: 500,
    borderRadius: '7px',
    px: { xs: 1.5, sm: 2 },
    py: { xs: 1, sm: 1.2 }
  };

  const getBusinessNamesTitle = (businessNames?: string[]) => {
    if (!businessNames || businessNames.length === 0) {
      return 'No businesses';
    }

    return (
      <Box
        sx={{
          maxHeight: 250,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#212020', borderRadius: 2 },
          '&::-webkit-scrollbar-track': { backgroundColor: '#2c2b2b' }
        }}
      >
        <Typography sx={{ fontSize: '0.90rem', fontWeight: 'bold', mb: 0.5 }}>Business using this package:</Typography>
        {businessNames.map((name, index) => (
          <Typography key={index} sx={{ fontSize: '0.85rem', lineHeight: 1.4 }}>
            • {name}
          </Typography>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      {/* Header */}
      <Box
        display='flex'
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={{ xs: 2, sm: 0 }}
        mb={2}
      >
        <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Typography variant='h6' sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
            Create Your Pricing Packages
          </Typography>
          <Typography color='secondary' variant='body2' mt={0.5} sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
            Packages help track usage, bill reports, and give a clear view of your earnings.
          </Typography>
        </Box>
        <Button
          variant='contained'
          size='small'
          onClick={() => navigate('add')}
          sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: '150px' } }}
        >
          + Add Package
        </Button>
      </Box>

      {isLoading ? (
        <PackageLoader />
      ) : (
        packagesData?.data.map((pkg) => {
          return (
            <Paper
              elevation={0}
              key={pkg.id}
              sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, border: '1px solid #eaeaea', borderRadius: 2 }}
            >
              <Box
                display='flex'
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent='space-between'
                gap={{ xs: 2, sm: 1 }}
              >
                <Box
                  display='flex'
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  gap={{ xs: 1, sm: 1 }}
                  flexWrap='wrap'
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  <Box display='flex' alignItems='center' gap={1} flexWrap='wrap'>
                    <Typography fontWeight='600' sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {pkg.name}
                    </Typography>
                    {!pkg.public && <FiLock color='#777' fontSize={16} />}
                    <Chip
                      label={formatDollars(pkg.base_price, 9)}
                      color='primary'
                      sx={{
                        color: theme.palette.primary.dark,
                        bgcolor: theme.palette.primary.light,
                        borderRadius: 6,
                        fontWeight: 500,
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        height: { xs: '24px', sm: '32px' }
                      }}
                    />
                  </Box>
                  <Box
                    pl={{ xs: 0, sm: 1 }}
                    borderLeft={{ xs: 'none', sm: '1px solid #efeaeaff' }}
                    display='flex'
                    flexDirection='row'
                    alignItems='center'
                    gap={0.5}
                    color='secondary'
                  >
                    <Typography color='text.secondary' sx={{ fontSize: { xs: '12px', sm: '14px' } }} mr={1}>
                      {pkg?.available_minutes ?? 0} {pkg?.available_minutes ? 'Minutes' : 'Minute'} included
                    </Typography>
                    <MdBusiness color='#777' size={16} />
                    <Typography color='text.secondary' sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
                      <Tooltip title={getBusinessNamesTitle(pkg?.business_names)} placement='top' arrow>
                        <span style={{ cursor: 'pointer' }}>{pkg?.business_ids?.length ?? 0}</span>
                      </Tooltip>
                    </Typography>
                  </Box>
                </Box>

                <Box display='flex' alignItems='center' gap={0} flexShrink={0}>
                  <IconButton
                    size='small'
                    sx={{
                      padding: { xs: '4px', sm: '8px' },
                      '& svg': {
                        fontSize: { xs: '16px', sm: '20px' }
                      }
                    }}
                  >
                    <FaRegCopy />
                  </IconButton>
                  <IconButton
                    size='small'
                    onClick={() => navigate(`${pkg.id}`)}
                    sx={{
                      padding: { xs: '4px', sm: '8px' },
                      '& svg': {
                        fontSize: { xs: '18px', sm: '24px' }
                      }
                    }}
                  >
                    <MdOutlineEdit />
                  </IconButton>
                  {!pkg?.default && (
                    <IconButton
                      size='small'
                      sx={{
                        padding: { xs: '4px', sm: '8px' },
                        '& svg': {
                          fontSize: { xs: '18px', sm: '20px' }
                        }
                      }}
                    >
                      <RiInboxArchiveLine onClick={() => deleteBusinessPlan(String(pkg?.id))} />
                    </IconButton>
                  )}
                  <IconButton
                    size='small'
                    onClick={() => handleToggle(String(pkg.id))}
                    sx={{
                      padding: { xs: '4px', sm: '8px' },
                      '& svg': {
                        fontSize: { xs: '18px', sm: '24px' }
                      }
                    }}
                  >
                    {openPackage === String(pkg.id) ? <MdExpandLess /> : <MdExpandMore />}
                  </IconButton>
                </Box>
              </Box>

              {/* Expandable Features */}
              <Collapse in={openPackage === String(pkg?.id)} timeout='auto' unmountOnExit>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={{ xs: 1, sm: 1 }}>
                  {/* Additional Minutes */}
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box display='flex' alignItems='center' justifyContent='space-between' sx={cardStyle}>
                      <Box display='flex' alignItems='center' gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                        <FiClock size={18} />
                        <Typography
                          variant='body2'
                          fontWeight={500}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Additional Minutes
                        </Typography>
                      </Box>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          ml: 1,
                          flexShrink: 0
                        }}
                      >
                        {formatDollars(pkg?.additional_per_min_charge, 9)}/Min
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Included Warm Call Minutes */}
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box display='flex' alignItems='center' justifyContent='space-between' sx={cardStyle}>
                      <Box display='flex' alignItems='center' gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                        <FiClock size={18} />
                        <Typography
                          variant='body2'
                          fontWeight={500}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Included Warm Call Minutes
                        </Typography>
                      </Box>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          ml: 1,
                          flexShrink: 0
                        }}
                      >
                        {pkg?.available_warm_call_minutes ?? 0}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Additional Warm Call Minutes */}
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box display='flex' alignItems='center' justifyContent='space-between' sx={cardStyle}>
                      <Box display='flex' alignItems='center' gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                        <FiClock size={18} />
                        <Typography
                          variant='body2'
                          fontWeight={500}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Additional Warm Call
                        </Typography>
                      </Box>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          ml: 1,
                          flexShrink: 0
                        }}
                      >
                        {formatDollars(pkg?.additional_warm_call_charge, 9)}/Min
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Included AI Chats */}
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box display='flex' alignItems='center' justifyContent='space-between' sx={cardStyle}>
                      <Box display='flex' alignItems='center' gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                        <ChatLogsIcon size={18} />
                        <Typography
                          variant='body2'
                          fontWeight={500}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Included AI Chats
                        </Typography>
                      </Box>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          ml: 1,
                          flexShrink: 0
                        }}
                      >
                        {pkg?.available_chats}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* AI Chat Charges */}
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box display='flex' alignItems='center' justifyContent='space-between' sx={cardStyle}>
                      <Box display='flex' alignItems='center' gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                        <ChatLogsIcon size={18} />
                        <Typography
                          variant='body2'
                          fontWeight={500}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Additional AI Chat Charges
                        </Typography>
                      </Box>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          ml: 1,
                          flexShrink: 0
                        }}
                      >
                        {formatDollars(pkg?.additional_per_chat_charge, 9)}/message
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box display='flex' alignItems='center' justifyContent='space-between' sx={cardStyle}>
                      <Box display='flex' alignItems='center' gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                        <LuSettings size={18} />
                        <Typography
                          variant='body2'
                          fontWeight={500}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Included AI Tokens
                        </Typography>
                      </Box>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          ml: 1,
                          flexShrink: 0
                        }}
                      >
                        {pkg?.included_ai_tokens || 0}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box display='flex' alignItems='center' justifyContent='space-between' sx={cardStyle}>
                      <Box display='flex' alignItems='center' gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                        <LuSettings size={18} />
                        <Typography
                          variant='body2'
                          fontWeight={500}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Cost per 1K Additional Tokens
                        </Typography>
                      </Box>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          ml: 1,
                          flexShrink: 0
                        }}
                      >
                        {formatDollars(pkg?.additional_per_1k_ai_tokens_charge, 9)}/1K tokens
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Multilingual Charges */}
                  {pkg?.price_details?.multilingual_charge_per_min?.enabled && (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Box display='flex' alignItems='center' justifyContent='space-between' sx={cardStyle}>
                        <Box display='flex' alignItems='center' gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                          <GoGlobe size={18} />
                          <Typography
                            variant='body2'
                            fontWeight={500}
                            sx={{
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Multilingual Charges
                          </Typography>
                        </Box>
                        <Typography
                          variant='body2'
                          fontWeight={600}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            ml: 1,
                            flexShrink: 0
                          }}
                        >
                          {formatDollars(pkg?.price_details?.multilingual_charge_per_min?.unit_amount, 9)}/Min
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box display='flex' alignItems='center' justifyContent='space-between' sx={cardStyle}>
                      <Box display='flex' alignItems='center' gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                        <PackageLocationIcon size={16} />
                        <Typography
                          variant='body2'
                          fontWeight={500}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Included Locations
                        </Typography>
                      </Box>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          ml: 1,
                          flexShrink: 0
                        }}
                      >
                        {pkg?.included_locations || 0}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Integration Charges */}
                  {pkg?.price_details?.integration_charge?.enabled && (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Box display='flex' alignItems='center' justifyContent='space-between' sx={cardStyle}>
                        <Box display='flex' alignItems='center' gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                          <LuSettings size={18} />
                          <Typography
                            variant='body2'
                            fontWeight={500}
                            sx={{
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Integration Charges
                          </Typography>
                        </Box>
                        <Typography
                          variant='body2'
                          fontWeight={600}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            ml: 1,
                            flexShrink: 0
                          }}
                        >
                          {pkg?.price_details.integration_charge.enabled ? 'ON' : 'OFF'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box display='flex' alignItems='center' justifyContent='space-between' sx={cardStyle}>
                      <Box display='flex' alignItems='center' gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                        <PackageLocationIcon />
                        <Typography
                          variant='body2'
                          fontWeight={500}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Additional Location Charge
                        </Typography>
                      </Box>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          ml: 1,
                          flexShrink: 0
                        }}
                      >
                        {formatDollars(pkg?.additional_per_location_charge, 9)}/location
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Storage Charges */}
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box display='flex' alignItems='center' justifyContent='space-between' sx={cardStyle}>
                      <Box display='flex' alignItems='center' gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                        <RiHardDrive3Line size={18} />
                        <Typography
                          variant='body2'
                          fontWeight={500}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Included Storage
                        </Typography>
                      </Box>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          ml: 1,
                          flexShrink: 0
                        }}
                      >
                        {pkg?.included_storage || 0} GB
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box display='flex' alignItems='center' justifyContent='space-between' sx={cardStyle}>
                      <Box display='flex' alignItems='center' gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                        <RiHardDrive3Line size={18} />
                        <Typography
                          variant='body2'
                          fontWeight={500}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Additional Storage Charge
                        </Typography>
                      </Box>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          ml: 1,
                          flexShrink: 0
                        }}
                      >
                        {formatDollars(pkg?.additional_per_storage_charge, 9)}/GB
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Included Concurrency Per Location */}
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box display='flex' alignItems='center' justifyContent='space-between' sx={cardStyle}>
                      <Box display='flex' alignItems='center' gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                        <AddonsConcurrencyIcon />
                        <Typography
                          variant='body2'
                          fontWeight={500}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Included Concurrency Per Location
                        </Typography>
                      </Box>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          ml: 1,
                          flexShrink: 0
                        }}
                      >
                        {pkg?.included_concurrency_per_location ?? 3}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Collapse>
            </Paper>
          );
        })
      )}
    </Box>
  );
};

export default Packages;
